import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe2 } from 'lucide-react';
import { BR_TIMEZONES } from '@/components/TimezoneSelect';
import { ScheduleSelect } from './ScheduleSelect';
import { formatTimezoneLabel } from '@/lib/format/timezone';

export function QueueTimezoneSelect({ value, onChange, id = 'queue-timezone' }: { value: string; onChange: (value: string) => void; id?: string }) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language;
  const options = useMemo(() => {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
    const zones = new Set([
      value, ...BR_TIMEZONES.map((zone) => zone.value),
      ...(intl.supportedValuesOf?.('timeZone') ?? []),
    ]);
    const now = new Date();
    return [...zones].filter(Boolean).map((zone) => {
      let offset = '';
      try {
        offset = new Intl.DateTimeFormat(language, { timeZone: zone, timeZoneName: 'shortOffset' })
          .formatToParts(now).find((part) => part.type === 'timeZoneName')?.value ?? '';
      } catch { /* Preserve a saved timezone so it can be corrected in the form. */ }
      const brazil = BR_TIMEZONES.find((item) => item.value === zone);
      const city = brazil?.label.split(' — ')[1]?.split(' (')[0] ?? zone.split('/').slice(1).join(' / ').replace(/_/g, ' ');
      const displayZone = formatTimezoneLabel(zone);
      const friendly = displayZone !== zone;
      return { value: zone, label: `${friendly ? displayZone : city || zone}${offset ? ` · ${offset}` : ''}`, description: friendly ? city : zone, keywords: [zone, brazil?.label ?? ''] };
    });
  }, [language, value]);

  return <ScheduleSelect id={id} value={value} options={options} onChange={onChange}
    searchLabel={t('publishQueues.form.searchTimezone')}
    icon={<Globe2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />} />;
}
